import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiMail, FiUser, FiMessageSquare, FiSend, FiCheck, FiAlertCircle } from 'react-icons/fi';
import emailjs from '@emailjs/browser';
import './Contact.css';

const Contact = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: '',
        projectType: 'website'
    });
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState(null);

    // EmailJS configuration
    const EMAIL_SERVICE_ID = 'service_akt3esh'; // ✅ Your service ID
    const EMAIL_TEMPLATE_ID = 'template_rybyd5g'; // ✅ Your template ID
    const EMAIL_PUBLIC_KEY = 'reTl8QWHPB-4NPd6K'; // ✅ Your public key

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitError(null);

        try {
            // Send email using EmailJS
            const templateParams = {
                from_name: formData.name,
                from_email: formData.email,
                project_type: formData.projectType,
                message: formData.message,
                to_name: 'Paule Development',
                reply_to: formData.email,
                // Common EmailJS template variables
                user_name: formData.name,
                user_email: formData.email,
                user_message: formData.message
            };

            const result = await emailjs.send(
                EMAIL_SERVICE_ID,
                EMAIL_TEMPLATE_ID,
                templateParams,
                EMAIL_PUBLIC_KEY
            );

            console.log('Email sent successfully:', result);
            setIsSubmitted(true);
            setIsSubmitting(false);

            // Reset form after 5 seconds
            setTimeout(() => {
                setIsSubmitted(false);
                setFormData({
                    name: '',
                    email: '',
                    message: '',
                    projectType: 'website'
                });
            }, 5000);

        } catch (error) {
            console.error('Email sending failed:', error);
            setSubmitError('Failed to send message. Please try again or contact me directly.');
            setIsSubmitting(false);
            
            // Clear error after 5 seconds
            setTimeout(() => {
                setSubmitError(null);
            }, 5000);
        }
    };

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2
            }
        }
    };

    const itemVariants = {
        hidden: { y: 30, opacity: 0 },
        visible: {
            y: 0,
            opacity: 1,
            transition: {
                duration: 0.6,
                ease: "easeOut"
            }
        }
    };

    return (
        <section id="contact" className="contact">
            <div className="container">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    viewport={{ once: true }}
                    className="section-header"
                >
                    <h2 className="section-title">Let's Work Together</h2>
                    <p className="section-subtitle">
                        Ready to bring your ideas to life? Get in touch and let's discuss your next project.
                    </p>
                </motion.div>

                <div className="contact-content">
                    <motion.div
                        className="contact-info"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                    >
                        <motion.div className="info-card" variants={itemVariants}>
                            <FiMail className="info-icon" />
                            <h3>Email Me</h3>
                            <p>Ready to start your project? Drop me a line and I'll get back to you within 24 hours.</p>
                        </motion.div>

                        <motion.div className="info-card" variants={itemVariants}>
                            <FiUser className="info-icon" />
                            <h3>Professional Service</h3>
                            <p>High-quality development with attention to detail and commitment to deadlines.</p>
                        </motion.div>

                        <motion.div className="info-card" variants={itemVariants}>
                            <FiMessageSquare className="info-icon" />
                            <h3>Free Consultation</h3>
                            <p>Not sure what you need? Let's discuss your requirements and find the best solution.</p>
                        </motion.div>
                    </motion.div>

                    <motion.div
                        className="contact-form-container"
                        initial={{ opacity: 0, x: 30 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        viewport={{ once: true }}
                    >
                        <form className="contact-form" onSubmit={handleSubmit}>
                            <div className="form-group">
                                <label htmlFor="name">Your Name</label>
                                <input
                                    type="text"
                                    id="name"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    placeholder="Enter your full name"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email Address</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                    placeholder="your@email.com"
                                />
                            </div>

                            <div className="form-group">
                                <label htmlFor="projectType">Project Type</label>
                                <select
                                    id="projectType"
                                    name="projectType"
                                    value={formData.projectType}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="website">Website Development</option>
                                    <option value="game">Game Development</option>
                                    <option value="webapp">Web Application</option>
                                    <option value="mobile">Mobile App</option>
                                    <option value="ecommerce">E-commerce</option>
                                    <option value="custom">Custom Software</option>
                                    <option value="other">Other</option>
                                </select>
                            </div>

                            <div className="form-group">
                                <label htmlFor="message">Project Details</label>
                                <textarea
                                    id="message"
                                    name="message"
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                    rows="5"
                                    placeholder="Tell me about your project, requirements, timeline, and budget..."
                                />
                            </div>

                            <motion.button
                                type="submit"
                                className={`submit-btn ${isSubmitted ? 'success' : submitError ? 'error' : ''}`}
                                disabled={isSubmitting || isSubmitted}
                                whileHover={{ scale: isSubmitted ? 1 : 1.02 }}
                                whileTap={{ scale: isSubmitted ? 1 : 0.98 }}
                            >
                                {isSubmitting ? (
                                    <motion.div
                                        className="loading"
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                                    >
                                        <FiSend />
                                    </motion.div>
                                ) : isSubmitted ? (
                                    <>
                                        <FiCheck />
                                        Message Sent!
                                    </>
                                ) : submitError ? (
                                    <>
                                        <FiAlertCircle />
                                        Try Again
                                    </>
                                ) : (
                                    <>
                                        <FiSend />
                                        Send Message
                                    </>
                                )}
                            </motion.button>
                            
                            {submitError && (
                                <motion.div 
                                    className="error-message"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -10 }}
                                >
                                    <FiAlertCircle />
                                    {submitError}
                                </motion.div>
                            )}
                        </form>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};

export default Contact;
